import {defaultSerializeQueryArgs, fetchBaseQuery} from '@reduxjs/toolkit/query/react';

import appConfig from '@/config/appConfig.typed';
import authDataService from '@/services/auth/authDataService';
import iccResponseErrorCheckService from '@/services/icc/iccResponseErrorCheckService';
import {getMappedInitialLanguageCode} from '@/services/localization/localizationService';
import log from '@/services/logger/log';
import {logRequestError} from '@/services/logger/logService';
import urlUtils from '@/utils/urlUtils';

const getHeaders = async (headers) => {
    const authToken = await authDataService.getAuthToken();

    if (!authToken) return headers;

    const appVersion = appConfig.getAppVersion();
    const languageCode = getMappedInitialLanguageCode()?.toUpperCase();

    headers.set('Authorization', 'Bearer ' + authToken);
    headers.set('v', appVersion);
    headers.set('l', languageCode);

    return headers;
};

/** @returns {string} */
export const serializeQueryArgsWithLanguage = (args) => {
    const serializedArgs = defaultSerializeQueryArgs(args);
    const languageCode = getMappedInitialLanguageCode();

    return languageCode ? `${serializedArgs}(${languageCode})` : serializedArgs;
};

export const getBaseQuery = (baseUrlGetter) => {
    const baseQuery = async (args, api, extraOptions) => {
        const baseUrl = baseUrlGetter();
        const requestUrl = typeof args === 'string' ? args : args?.url;
        const url = requestUrl ? urlUtils.join(baseUrl, requestUrl) : baseUrl;
        const rawBaseQuery = fetchBaseQuery({
            baseUrl,
            prepareHeaders: getHeaders,
        });

        log.debug(`Request has started. Url: ${url}`);

        const result = await rawBaseQuery(args, api, extraOptions);
        const {error} = result || {};

        if (error) {
            const requestData = result?.meta?.request;
            const {method} = requestData || {};
            const logMessage = `requestApi: url: ${url}; requestConfig: {}, method: ${method}; error: ${JSON.stringify(error)}`;
            const statusCode = error?.status;

            logRequestError(statusCode, logMessage);

            await iccResponseErrorCheckService({status: statusCode, error});

            return baseQuery(args, api, extraOptions);
        }

        log.info(`Request has successful response. Url: ${url}`);

        return result;
    };

    return baseQuery;
};
